import { Col, message, Modal, Row, Table, Tabs, Tag } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { SearchOutlined, UnorderedListOutlined } from "@ant-design/icons";

import Text from "../../../components/Text/Text";
import Button from "../../../components/Button/Button";
import { GreyTableContainer } from "../../../components/Table/GreyTable";
import { FlexCol } from "../../../components/Container/Container";
import Input from "../../../components/Input/Input";
import { icon } from "../../../resource";
import { getOverCredit } from "../../../datasource/OrderCreditDatasource";
import {
  deleteTempCreditLimit,
  getOrderCreditLimit,
  getStatusRequestCredit,
} from "../../../datasource/CreditLimitDatasource";
import {
  CreditLimitStatus,
  CreditLimitStatusColor,
  CreditLimitStatusLabel,
} from "../../../definitions/creditLimit";
import {
  AllOrderCreditLimitEntity_INIT,
  type AllOrderCreditLimitEntity,
  type creditApproversEntity,
  type orderCreditEntity,
  type soApproversEntity,
} from "../../../entities/OrderCreditEntity";
import { numberFormatter } from "../../../utility/Formatter";
import { normalizeStatus, STATUS_CREDIT } from "../../../definitions/creditStatus";
import StatusBadge from "../../../components/Badge/StatusBadge";
import { ActionBtn } from "../../../components/Button/TableActionButton";
import ModalCheckStatusCredit from "../../../components/Modal/ModalCheckStatusCredit";

const DATE_PATTERN = "DD/MM/YYYY, HH:mm";
const PAGE_SIZE = 10;
const ICON_SIZE = { width: 15, height: 16 };
const STATUS_ENUMS = {
  ORDER: [
    { key: "PENDING", label: "รออนุมัติ" },
    { key: "APPROVE", label: "อนุมัติแล้ว" },
    { key: "REJECT", label: "ไม่อนุมัติ" },
  ],
  CREDIT: [
    { key: "WAITING", label: "รออนุมัติ" },
    { key: "APPROVED", label: "อนุมัติแล้ว" },
    { key: "REJECTED", label: "ไม่อนุมัติ" },
  ],
};
interface OverCreditData {
  temporaryCreditLimitId: string;
  temporaryCreditLimitCode: string;
  creditLimitRequest: number;
  creditLimitRequestPercent?: number;
  remarkRequest: string;
  status: CreditLimitStatus;
  createdAt: string;
  createdBy: string;
}

interface CreditDataResponse {
  data: OverCreditData[];
  count: number;
  countStatusTemp: {
    countWaiting: number;
    lastUpdatedAt: string;
  };
}

interface UserProfile {
  company: string;
  firstname: string;
  lastname: string;
}

type TabKey = "ORDER" | "CREDIT";

export const OverCreditOrderCreditDetailTab: React.FC = () => {
  const userProfile = useMemo<UserProfile | null>(() => {
    try {
      const profile = localStorage.getItem("profile");
      return profile ? JSON.parse(profile) : null;
    } catch (error) {
      console.error("Error parsing user profile:", error);
      return null;
    }
  }, []);

  const customerId = useMemo(() => {
    const pathSplit = window.location.pathname.split("/");
    const id = parseInt(pathSplit[3], 10);
    return isNaN(id) ? null : id;
  }, []);
  const [approverCreditRequest, setApproverCreditRequest] = useState<{
    creditApprovers: creditApproversEntity | null;
    order: orderCreditEntity | null;
    soApprovers: soApproversEntity | null;
  }>({ creditApprovers: null, order: null, soApprovers: null });
  const [activeTabKey, setActiveTabKey] = useState<TabKey>("ORDER");
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState<AllOrderCreditLimitEntity>(
    AllOrderCreditLimitEntity_INIT,
  );
  const [modalCheck, setModalCheck] = useState<any>();
  const [creditData, setCreditData] = useState<CreditDataResponse | null>(null);
  const [status, setStatus] = useState<any>(activeTabKey === "ORDER" ? "PENDING" : "WAITING");
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState<string>("");

  useEffect(() => {
    setPage(1);
  }, [status, searchInput]);

  const fetchData = useCallback(async () => {
    if (!customerId) {
      console.error("Invalid customer ID");
      return;
    }

    if (!userProfile?.company) {
      console.error("User profile or company not found");
      return;
    }

    setLoading(true);
    try {
      if (activeTabKey === "ORDER") {
        const result = await getOrderCreditLimit(
          userProfile.company,
          status,
          searchInput,
          "SO",
          undefined,
          undefined,
          undefined,
          undefined,
          page,
          PAGE_SIZE,
          customerId.toString(),
        );
        setOrderData(result);
      } else {
        const result = await getOverCredit({
          customerCompanyId: customerId,
          status,
          search: searchInput,
          page,
          take: PAGE_SIZE,
        });
        setCreditData(result);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("เกิดข้อผิดพลาดในการโหลดข้อมูล");
    } finally {
      setLoading(false);
    }
  }, [activeTabKey, customerId, status, searchInput, page, userProfile]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const checkApproveCredit = async () => {
    try {
      const response = await getStatusRequestCredit(modalCheck?.id);
      setApproverCreditRequest(response);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (modalCheck?.open) {
      checkApproveCredit();
    }
  }, [modalCheck]);
  const handleDelete = useCallback(
    async (temporaryCreditLimitId: string) => {
      if (!userProfile) {
        message.error("ไม่พบข้อมูลผู้ใช้");
        return;
      }

      const { firstname, lastname } = userProfile;
      const fullName = `${firstname} ${lastname}`;

      Modal.confirm({
        icon: null,
        title: "ต้องการลบคำขอวงเงินเกิน",
        content: "โปรดยืนยันการลบข้อมูลคำขอวงเงินเกิน",
        onOk: async () => {
          try {
            const res = await deleteTempCreditLimit(temporaryCreditLimitId, fullName);

            if (res.success) {
              message.success("ลบข้อมูลสำเร็จ");
              setPage(1);
              await fetchData();
            } else {
              message.error("เกิดข้อผิดพลาดในการลบ");
            }
          } catch (error) {
            console.error("Error deleting temp credit limit:", error);
            message.error("เกิดข้อผิดพลาดในการลบ");
          }
        },
      });
    },
    [userProfile, fetchData],
  );

  const handleTabChange = useCallback((key: string) => {
    setActiveTabKey(key as TabKey);
    setSearchInput("");
    if (key === "CREDIT") {
      setStatus("WAITING");
    } else if (key === "ORDER") {
      setStatus("PENDING");
    }

    setPage(1);
  }, []);

  const handleStatusChange = (key: CreditLimitStatus) => {
    setStatus(key);
    setPage(1);
  };

  const handleSearchInputChange = useCallback((value: string) => {
    setSearchInput(value);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const countPending =
    activeTabKey === "ORDER" ? orderData?.countPending : creditData?.countStatusTemp?.countWaiting;

  const totalCount = activeTabKey === "ORDER" ? orderData?.count || 0 : creditData?.count || 0;

  const lastUpdate =
    activeTabKey === "ORDER" ? orderData?.lastUpdate : creditData?.countStatusTemp?.lastUpdatedAt;

  const tableData = activeTabKey === "ORDER" ? orderData?.data || [] : creditData?.data || [];

  const columns = useMemo<ColumnsType<OverCreditData>>(() => {
    const baseColumns: ColumnsType<OverCreditData> = [
      {
        title: "วันที่อัปเดท",
        dataIndex: activeTabKey === "ORDER" ? "updateAt" : "createdAt",
        key: "createdAt",
        render: (createdAt: string, row: OverCreditData) => (
          <FlexCol>
            <Text>{dayjs(createdAt).format(DATE_PATTERN)}</Text>
            <Text color='Text3' level={6}>
              {row.createdBy}
            </Text>
          </FlexCol>
        ),
      },
      {
        title: activeTabKey === "ORDER" ? "รหัสคำสั่งซื้อ" : "รหัสคำขอ",
        dataIndex: activeTabKey === "ORDER" ? "orderNo" : "temporaryCreditLimitCode",
        key: "temporaryCreditLimitCode",
        render: (val: string) => <Text>{val || "-"}</Text>,
      },
      {
        title: "วงเงินที่ขอ",
        dataIndex: activeTabKey === "ORDER" ? "requestedOverCredit" : "creditLimitRequest",
        key: "creditLimitRequest",
        render: (creditLimitRequest: number, row: any) => (
          <FlexCol>
            <Text>{numberFormatter(creditLimitRequest, 2)}</Text>
            <Text color='Text3' level={6}>
              (
              {numberFormatter(
                activeTabKey === "ORDER"
                  ? row.percentOverCredit
                  : row.creditLimitRequestPercent || 0,
                2,
              )}
              %)
            </Text>
          </FlexCol>
        ),
      },
      {
        title: "เหตุผลการขอ",
        dataIndex: activeTabKey === "ORDER" ? "reason" : "remarkRequest",
        key: "remarkRequest",
        render: (remarkRequest: string) => <Text>{remarkRequest || "-"}</Text>,
      },
      {
        title: "สถานะ",
        dataIndex: activeTabKey === "ORDER" ? "thisStatus" : "status",
        key: "status",
        render: (status: any, row: any) => {
          const normalizedStatus = normalizeStatus(status);
          const meta = STATUS_CREDIT[normalizedStatus] || {};
          return activeTabKey === "ORDER" ? (
            <StatusBadge label={meta.label || "-"} bgColor={meta.color || "gray"} />
          ) : (
            <Tag color={CreditLimitStatusColor[status]} style={{ borderRadius: 12 }}>
              <Text color='white' fontWeight={600} fontSize={14}>
                {CreditLimitStatusLabel[status]}
              </Text>
            </Tag>
          );
        },
      },
      ...(activeTabKey === "ORDER"
        ? [
            {
              title: "",
              dataIndex: "action",
              key: "action",
              width: "10%",
              render: (value: any, row: any, index: number) => {
                return {
                  children: (
                    <Row gutter={10} style={{ gap: 6 }} justify='start'>
                      <ActionBtn
                        onClick={() => setModalCheck({ open: true, id: row.orderId })}
                        icon={
                          <img
                            src={icon.Audit}
                            style={{ width: 20, height: 20, fontWeight: 700 }}
                          />
                        }
                      />
                    </Row>
                  ),
                };
              },
            },
          ]
        : []),
    ];

    baseColumns.push({
      title: "",
      dataIndex: "status",
      key: "action",
      hidden: activeTabKey !== "CREDIT",
      render: (status: CreditLimitStatus, row: OverCreditData) => {
        const isDisabled = status !== "WAITING";
        const deleteIcon = isDisabled ? icon.deleteText4 : icon.delete;

        return (
          <Row justify='space-evenly'>
            <Col>
              <div
                className='btn btn-icon btn-light btn-hover-primary btn-sm'
                onClick={isDisabled ? undefined : () => handleDelete(row.temporaryCreditLimitId)}
                style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
              >
                <span className='svg-icon svg-icon-primary svg-icon-2x'>
                  <img src={deleteIcon} style={ICON_SIZE} alt='Delete' />
                </span>
              </div>
            </Col>
          </Row>
        );
      },
    });

    return baseColumns.filter((col) => !col.hidden);
  }, [activeTabKey, handleDelete]);

  const statusButtons = useMemo(
    () =>
      STATUS_ENUMS[activeTabKey].map((s) => ({
        ...s,
        label:
          s.key === "PENDING" || s.key === "WAITING"
            ? `${s.label} (${countPending || 0})`
            : s.label,
      })),
    [activeTabKey, countPending],
  );

  const renderTabContent = useCallback(
    () => (
      <>
        <Row gutter={8} style={{ margin: "12px 0px" }} align='middle'>
          <Col>
            <Row gutter={8}>
              {statusButtons.map(({ key, label }) => (
                <Col key={key}>
                  <Button
                    title={label}
                    typeButton={key === status ? "navy-blue" : "light-navy-blue"}
                    onClick={() => handleStatusChange(key as CreditLimitStatus)}
                  />
                </Col>
              ))}
            </Row>
          </Col>
          <Col flex='auto' />
          <Col>
            <Input
              style={{ minWidth: 320 }}
              placeholder={activeTabKey === "ORDER" ? "ค้นหารหัสคำสั่งซื้อ" : "ค้นหารหัสคำขอ"}
              suffix={<SearchOutlined />}
              value={searchInput}
              allowClear
              onPressEnter={(e) => {
                const value = (e.target as HTMLInputElement).value.trim();
                handleSearchInputChange(value);
              }}
              onChange={(e) => {
                const value = e.target.value;
                handleSearchInputChange(value);
              }}
            />
          </Col>
        </Row>
        <Row justify='end'>
          <Text>ทั้งหมด {totalCount} รายการ</Text>
        </Row>
        <br />
        <GreyTableContainer>
          <Table
            className='rounded-lg'
            columns={columns}
            dataSource={tableData as any}
            pagination={{
              pageSize: PAGE_SIZE,
              current: page,
              total: totalCount,
              showSizeChanger: false,
              onChange: handlePageChange,
              position: ["bottomCenter"],
            }}
            loading={loading}
            size='large'
            tableLayout='fixed'
          />
        </GreyTableContainer>
      </>
    ),
    [
      statusButtons,
      status,
      searchInput,
      columns,
      page,
      loading,
      totalCount,
      tableData,
      activeTabKey,
      handleStatusChange,
      handleSearchInputChange,
      handlePageChange,
    ],
  );

  const tabsItems = useMemo(
    () => [
      {
        label: (
          <Text fontSize={14} fontWeight={700}>
            คำสั่งซื้อ
          </Text>
        ),
        key: "ORDER",
        children: renderTabContent(),
      },
      {
        label: (
          <Text fontSize={14} fontWeight={700}>
            วงเงินชั่วคราว
          </Text>
        ),
        key: "CREDIT",
        children: renderTabContent(),
      },
    ],
    [renderTabContent],
  );

  return (
    <>
      <Row gutter={16}>
        <Col span={20}>
          <Text fontWeight={700}>วงเงินเกิน (Over Credit)</Text>
          <br />
          <Text level={6}>
            อัปเดตล่าสุดเมื่อ {lastUpdate ? dayjs(lastUpdate).format(DATE_PATTERN) : "-"}
          </Text>
        </Col>
      </Row>
      <Tabs activeKey={activeTabKey} items={tabsItems} onChange={handleTabChange} />
      <ModalCheckStatusCredit
        open={modalCheck?.open}
        onClose={() => setModalCheck(undefined)}
        data={approverCreditRequest}
        companyCode={userProfile?.company || ""}
      />
    </>
  );
};
